'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ICEBREAKERS,
  NEVER_HAVE_I_EVER,
  TRIVIA_QUESTIONS,
  TRUTH_OR_DARE,
  WOULD_YOU_RATHER,
  type PartyQuestion,
} from '@/lib/party-questions';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

export type QuestionGeneratorVariant =
  | 'would-you-rather-questions'
  | 'never-have-i-ever-questions'
  | 'truth-or-dare-questions'
  | 'icebreaker-questions'
  | 'trivia-questions';

const POOLS: Record<QuestionGeneratorVariant, PartyQuestion[]> = {
  'would-you-rather-questions': WOULD_YOU_RATHER,
  'never-have-i-ever-questions': NEVER_HAVE_I_EVER,
  'truth-or-dare-questions': TRUTH_OR_DARE,
  'icebreaker-questions': ICEBREAKERS,
  'trivia-questions': TRIVIA_QUESTIONS,
};

const COPY: Record<QuestionGeneratorVariant, { heading: string; blurb: string; badge: string }> = {
  'would-you-rather-questions': {
    heading: 'Would you rather…',
    blurb:
      'Draw a dilemma, argue it out, draw the next. Questions never repeat until the list runs dry.',
    badge: 'Party',
  },
  'never-have-i-ever-questions': {
    heading: 'Never have I ever…',
    blurb:
      'Draw a prompt, see who has done it, draw the next. Family-friendly list, no repeats until it runs dry.',
    badge: 'Party',
  },
  'truth-or-dare-questions': {
    heading: 'Truth or dare',
    blurb:
      'Filter to truths, dares, or both, then draw. Everything here is safe to play with friends or family in a living room.',
    badge: 'Party',
  },
  'icebreaker-questions': {
    heading: 'Icebreaker questions',
    blurb:
      'Work-safe openers for standups, workshops, and first meetings. Draw one per person and go around the room.',
    badge: 'Team',
  },
  'trivia-questions': {
    heading: 'Trivia questions',
    blurb:
      'Draw a question, let everyone guess, then reveal the answer. Categories cover geography, science, history, culture, and food.',
    badge: 'Trivia',
  },
};

function pickIndex(count: number): number {
  const buffer = new Uint32Array(1);
  crypto.getRandomValues(buffer);
  return buffer[0] % count;
}

function labelFor(category: string): string {
  return category[0].toUpperCase() + category.slice(1);
}

export default function BrowserQuestionGenerator({
  variant,
}: {
  variant: QuestionGeneratorVariant;
}) {
  const pool = POOLS[variant];
  const copy = COPY[variant];
  const isTrivia = variant === 'trivia-questions';
  const usageReported = useRef(false);

  const categories = useMemo(() => {
    const seen: string[] = [];
    for (const question of pool) {
      if (!seen.includes(question.category)) seen.push(question.category);
    }
    return ['all', ...seen];
  }, [pool]);

  const [category, setCategory] = useState('all');
  const [usedTexts, setUsedTexts] = useState<string[]>([]);
  const [current, setCurrent] = useState<PartyQuestion | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const filtered = useMemo(
    () => pool.filter((question) => category === 'all' || question.category === category),
    [pool, category],
  );

  useEffect(() => {
    if (!usageReported.current && current) {
      usageReported.current = true;
      void consumeToolUsage(variant, '').catch(() => undefined);
    }
  }, [current, variant]);

  function draw() {
    // Draw without repeats until the filtered pool is exhausted, then reset.
    const remaining = filtered.filter((question) => !usedTexts.includes(question.text));
    const source = remaining.length ? remaining : filtered;
    if (!source.length) return;
    const next = source[pickIndex(source.length)];
    setCurrent(next);
    setUsedTexts(remaining.length ? [...usedTexts, next.text] : [next.text]);
    setRevealed(false);
    setCopied(false);
  }

  async function copyCurrent() {
    if (!current) return;
    const text =
      isTrivia && current.answer ? `${current.text}\nAnswer: ${current.answer}` : current.text;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>{copy.heading}</h2>
          <p>{copy.blurb}</p>
        </div>
        <span className="tool-live-badge">{copy.badge}</span>
      </div>

      <div className="tool-live-grid">
        <div
          className="tool-live-uploader"
          style={{ alignItems: 'center', justifyContent: 'center', minHeight: 200 }}
        >
          {current ? (
            <>
              <p
                aria-live="polite"
                style={{ fontSize: '1.45rem', lineHeight: 1.5, maxWidth: 560, textAlign: 'center' }}
              >
                {current.text}
              </p>
              {isTrivia && current.answer ? (
                revealed ? (
                  <p style={{ fontSize: '1.15rem', marginTop: '0.75rem', textAlign: 'center' }}>
                    <strong>Answer:</strong> {current.answer}
                  </p>
                ) : (
                  <button
                    className="button button-ghost"
                    onClick={() => setRevealed(true)}
                    style={{ marginTop: '0.75rem' }}
                    type="button"
                  >
                    Reveal the answer
                  </button>
                )
              ) : null}
            </>
          ) : (
            <p className="tool-live-panel-note">
              Press the button and the first question appears here.
            </p>
          )}
        </div>

        <aside className="tool-live-panel">
          <label>
            Category
            <select
              onChange={(event) => {
                setCategory(event.target.value);
                setUsedTexts([]);
              }}
              value={category}
            >
              {categories.map((option) => (
                <option key={option} value={option}>
                  {option === 'all' ? 'All questions' : labelFor(option)}
                </option>
              ))}
            </select>
          </label>

          <div className="tool-live-stats">
            <div>
              <strong>
                {usedTexts.length}/{filtered.length}
              </strong>
              <span>questions drawn in this round</span>
            </div>
          </div>

          {current ? (
            <button
              className="button button-ghost"
              onClick={() => void copyCurrent()}
              type="button"
            >
              {copied ? 'Copied!' : 'Copy question'}
            </button>
          ) : null}

          <p className="tool-live-panel-note">
            Playing in a group? Open the Wheel Spinner in another tab with everyone’s names and spin
            for who answers next.
          </p>
        </aside>
      </div>

      <div className="hero-actions tool-live-actions">
        <button className="button" onClick={draw} type="button">
          {current ? 'Next question' : 'Draw the first question'}
        </button>
      </div>
    </article>
  );
}
